import { subDays } from 'date-fns';
import { type AugmentedAccount, PlanFeature } from '@/services/auth/types';

export type ActivityHistoryKind = 'message' | 'profile';

type ActivityHistoryWindow = {
  includedDays: number;
  addonDays: number;
};

// Profile activity tops out at 30 days, whatever the account pays.
export const ACTIVITY_HISTORY_WINDOWS = {
  message: { includedDays: 30, addonDays: 365 },
  profile: { includedDays: 7, addonDays: 30 },
} satisfies Record<ActivityHistoryKind, ActivityHistoryWindow>;

export type ActivityHistory = ActivityHistoryWindow & {
  heading: string;
  /** Unix seconds. */
  lookbackStart: number;
  /** Unix seconds. */
  addonLookbackStart: number;
};

const WINDOW_GATES = {
  message: PlanFeature.MessageActivityLookbackDays,
  profile: PlanFeature.ActivityLogDays,
} satisfies Record<ActivityHistoryKind, PlanFeature>;

/**
 * The account's included days from its plan gate. Without an enabled gate that has a positive limit,
 * the plan default applies.
 */
export function gatedIncludedDays(account: AugmentedAccount, kind: ActivityHistoryKind): number {
  const gate = account.planFeatures[WINDOW_GATES[kind]];
  const limit = gate?.enabled === true ? gate.limit : false;
  if (typeof limit !== 'number' || limit <= 0) return ACTIVITY_HISTORY_WINDOWS[kind].includedDays;
  return limit;
}

const toUnixSeconds = (date: Date) => Math.floor(date.getTime() / 1000);

/**
 * The window includes today, so a 7-day history starts 6 days back. `includedDays` overrides the
 * plan default with the account's own gate.
 */
export function activityHistory(
  kind: ActivityHistoryKind,
  now: Date = new Date(),
  includedDays: number = ACTIVITY_HISTORY_WINDOWS[kind].includedDays,
): ActivityHistory {
  const window = { ...ACTIVITY_HISTORY_WINDOWS[kind], includedDays };
  return {
    ...window,
    heading: kind === 'message' ? 'Message activity history' : 'Profile activity history',
    lookbackStart: toUnixSeconds(subDays(now, window.includedDays - 1)),
    addonLookbackStart: toUnixSeconds(subDays(now, window.addonDays - 1)),
  };
}
